import React, { useMemo, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import styled from "@emotion/styled";
import DeleteSearchDialog from "../DeleteSearchDialog";
import FilterChip from "./FilterChip";
import FilterMenu from "./FilterMenu";
import SearchResultsToolbar from "./SearchResultsToolbar";
import { FILTER_IDS, HORIZONTAL_CHIP_ORDER } from "./filterIds";
import { formatResultsCountLabel } from "./formatResultsCount";
import { renderFilterPanel } from "./renderFilterPanel";
import useFilterController from "./useFilterController";
import { useFilterControllerContext } from "./FilterControllerContext";

const Bar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "nowrap",
  padding: "12px",
  borderRadius: 14,
  overflow: "visible",
  backgroundColor: theme.palette.mode === "dark" ? "#1E1E1E" : "#FFFFFF",
  border: "none",
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 2px 4px rgba(0, 0, 0, 0.35)"
      : "0 2px 4px rgba(88, 89, 91, 0.12)",
  "@media (max-width: 1100px)": {
    gap: 8,
    padding: "12px",
  },
  "@media (max-width: 720px)": {
    flexWrap: "wrap",
    rowGap: 10,
    borderRadius: 12,
  },
}));

const ChipRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "nowrap",
  flex: "1 1 auto",
  minWidth: 0,
  // Room for hover lift + thicker active borders; overflow-x alone would clip them.
  paddingTop: 4,
  paddingBottom: 4,
  marginTop: -4,
  marginBottom: -4,
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  "@media (max-width: 720px)": {
    width: "100%",
  },
}));

const ResultsCounter = styled(Typography)(({ theme }) => ({
  flexShrink: 0,
  marginLeft: 4,
  padding: "0 4px",
  textAlign: "right",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.3,
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
  "@media (max-width: 720px)": {
    marginLeft: "auto",
  },
}));

const ActionsRow = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 6,
  flexWrap: "nowrap",
  flexShrink: 0,
  marginLeft: "auto",
  "@media (max-width: 720px)": {
    width: "100%",
    justifyContent: "flex-end",
    marginLeft: 0,
  },
}));

const VerticalRule = styled(Divider)(({ theme }) => ({
  height: 28,
  alignSelf: "center",
  borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(88,89,91,0.18)",
}));

function HorizontalFilterBarView({
  controller,
  ownDeleteDialog = true,
  layoutTestId = "filter-layout-v2",
  isNaturalLanguageMode = false,
  resultView,
  onResultViewChange,
  totalRows = 0,
  showResultsCount = true,
  onExport,
  isExporting = false,
  exportDisabled = false,
  exportTitle,
  onSaveSearch,
  saveSearchDisabled = false,
  saveSearchLabel = "Save search",
  showNaturalLanguageFilter = false,
}) {
  const theme = useTheme();
  const chipRefs = useRef({});

  const {
    activeFilter,
    toggleFilter,
    closeFilter,
    summaries,
    showClearFilters,
    filtersDisabled,
    clearFilters,
    panelContext,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    setItemToDelete,
    confirmDelete,
    isDeleting,
    isEditingSavedSearch = false,
    editingSavedSearchTitle = "",
    setEditingSavedSearchTitle,
    cancelEditSearch,
    confirmEditSearch,
    isSavingEdit = false,
    editSearchError = null,
    canSaveEditSearch = false,
    hasUncommittedFilters = false,
  } = controller;

  const resultsCountLabel = useMemo(() => formatResultsCountLabel(totalRows), [totalRows]);
  const menuOpen = Boolean(activeFilter);
  const activeAnchor = activeFilter ? chipRefs.current[activeFilter] : null;
  const isDark = theme.palette.mode === "dark";
  const filterOrder = showNaturalLanguageFilter && !HORIZONTAL_CHIP_ORDER.includes(FILTER_IDS.NATURAL_LANGUAGE)
    ? [...HORIZONTAL_CHIP_ORDER, FILTER_IDS.NATURAL_LANGUAGE]
    : HORIZONTAL_CHIP_ORDER;

  const chip = (id, summary, options = {}) => (
    <Box
      key={id}
      ref={(node) => {
        chipRefs.current[id] = node;
      }}
      sx={{ display: "inline-flex" }}
    >
      <FilterChip
        id={`filter-chip-${id}`}
        label={summary.displayLabel || summary.label}
        aria-label={summary.label}
        active={activeFilter === id}
        hasValue={summary.isActive}
        count={summary.count}
        variant={summary.variant || (summary.isActive ? "single" : "empty")}
        disabled={isEditingSavedSearch || options.disabled}
        disabledLook={options.disabledLook}
        aria-controls={activeFilter === id ? "horizontal-filter-menu" : undefined}
        onClick={() => toggleFilter(id)}
      />
    </Box>
  );

  return (
    <Box data-testid={layoutTestId}>
      <Bar data-testid="horizontal-filter-bar">
        {isEditingSavedSearch ? (
          <TextField
            size="small"
            label="Saved search title"
            value={editingSavedSearchTitle}
            onChange={(event) => setEditingSavedSearchTitle?.(event.target.value)}
            disabled={isSavingEdit}
            error={Boolean(editSearchError)}
            helperText={editSearchError || undefined}
            FormHelperTextProps={{ sx: { position: "absolute", top: "100%", m: 0 } }}
            inputProps={{
              "data-testid": "edit-saved-search-title-input",
              "aria-label": "Saved search title",
            }}
            sx={{
              flex: "0 1 220px",
              minWidth: 160,
              maxWidth: 280,
              mr: 0.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
                fontSize: 13,
                fontWeight: 600,
              },
              "& .MuiInputLabel-root": {
                fontSize: 12,
                fontWeight: 600,
              },
            }}
          />
        ) : null}

        <ChipRow>
          {filterOrder.map((id) =>
            chip(id, summaries[id], {
              disabledLook:
                !isEditingSavedSearch && id !== FILTER_IDS.NATURAL_LANGUAGE
                  ? filtersDisabled
                  : false,
            })
          )}
          {showClearFilters && !isNaturalLanguageMode && !isEditingSavedSearch && (
            <Button
              type="button"
              onClick={clearFilters}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 12,
                color: "#D80000",
                minWidth: 0,
                px: 0.75,
                flexShrink: 0,
              }}
            >
              Clear
            </Button>
          )}
        </ChipRow>

        {hasUncommittedFilters ? (
          <Typography
            data-testid="filter-uncommitted-indicator"
            sx={{ fontSize: 12, color: "warning.main", whiteSpace: "nowrap" }}
          >
            Changes not applied
          </Typography>
        ) : null}

        {isEditingSavedSearch ? (
          <ActionsRow data-testid="edit-saved-search-actions">
            <Button
              type="button"
              onClick={cancelEditSearch}
              disabled={isSavingEdit}
              data-testid="edit-saved-search-cancel"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                color: theme.palette.text.secondary,
                minWidth: 0,
                px: 1.25,
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={confirmEditSearch}
              disabled={isSavingEdit || !canSaveEditSearch}
              data-testid="edit-saved-search-save"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: 13,
                minWidth: 72,
                px: 1.5,
                borderRadius: "10px",
                boxShadow: "none",
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: "#3B22D4",
                  boxShadow: "none",
                },
              }}
            >
              {isSavingEdit ? <CircularProgress size={16} color="inherit" /> : "Save"}
            </Button>
          </ActionsRow>
        ) : (
          <>
            {showResultsCount ? (
              <ResultsCounter
                component="div"
                data-testid="results-counter"
                aria-label={`Showing ${resultsCountLabel}`}
              >
                Showing
                <br />
                {resultsCountLabel}
              </ResultsCounter>
            ) : null}

            <ActionsRow>

              <VerticalRule orientation="vertical" flexItem />

              <SearchResultsToolbar
                actionsOnly
                resultView={resultView}
                onResultViewChange={onResultViewChange}
                onExport={onExport}
                isExporting={isExporting}
                exportDisabled={exportDisabled}
                exportTitle={exportTitle}
                onSaveSearch={onSaveSearch}
                saveSearchDisabled={saveSearchDisabled}
                saveSearchLabel={saveSearchLabel}
              />
            </ActionsRow>
          </>
        )}
      </Bar>

      {isNaturalLanguageMode && (
        <Typography
          variant="body2"
          sx={{
            mt: 1.25,
            color: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          Natural language mode is active. Terms, industry, location, and size filters are ignored
          and disabled until the natural language query is cleared.
        </Typography>
      )}

      <FilterMenu
        id="horizontal-filter-menu"
        open={menuOpen}
        anchorEl={activeAnchor}
        onClose={closeFilter}
        width={activeFilter === FILTER_IDS.NATURAL_LANGUAGE ? 440 : undefined}
      >
        {renderFilterPanel(activeFilter, panelContext)}
      </FilterMenu>

      {ownDeleteDialog ? (
        <DeleteSearchDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setItemToDelete(null);
          }}
          onConfirm={confirmDelete}
          searchName={itemToDelete?.name}
          isDeleting={isDeleting}
        />
      ) : null}
    </Box>
  );
}

function HorizontalFilterBarStandalone(props) {
  const {
    layoutTestId,
    resultView,
    onResultViewChange,
    totalRows,
    showResultsCount,
    onExport,
    isExporting,
    exportDisabled,
    exportTitle,
    onSaveSearch,
    saveSearchDisabled,
    saveSearchLabel,
    showNaturalLanguageFilter,
    isNaturalLanguageMode,
    ...filterProps
  } = props;

  const controller = useFilterController({
    ...filterProps,
    isNaturalLanguageMode,
  });

  return (
    <HorizontalFilterBarView
      controller={controller}
      ownDeleteDialog
      layoutTestId={layoutTestId}
      isNaturalLanguageMode={isNaturalLanguageMode}
      resultView={resultView}
      onResultViewChange={onResultViewChange}
      totalRows={totalRows}
      showResultsCount={showResultsCount}
      onExport={onExport}
      isExporting={isExporting}
      exportDisabled={exportDisabled}
      exportTitle={exportTitle}
      onSaveSearch={onSaveSearch}
      saveSearchDisabled={saveSearchDisabled}
      saveSearchLabel={saveSearchLabel}
      showNaturalLanguageFilter={showNaturalLanguageFilter}
    />
  );
}

/**
 * V2 / V4 horizontal filter shell.
 * Uses a shared FilterControllerProvider when present (V4 banner owns saved searches).
 */
export default function HorizontalFilterBar(props) {
  const sharedController = useFilterControllerContext();
  const {
    layoutTestId = "filter-layout-v4",
    resultView,
    onResultViewChange,
    totalRows,
    showResultsCount = true,
    onExport,
    isExporting,
    exportDisabled,
    exportTitle,
    onSaveSearch,
    saveSearchDisabled,
    saveSearchLabel = "Save search",
    showNaturalLanguageFilter = false,
    isNaturalLanguageMode = false,
  } = props;

  if (sharedController) {
    return (
      <HorizontalFilterBarView
        controller={sharedController}
        ownDeleteDialog={false}
        layoutTestId={layoutTestId}
        isNaturalLanguageMode={isNaturalLanguageMode}
        resultView={resultView}
        onResultViewChange={onResultViewChange}
        totalRows={totalRows}
        showResultsCount={showResultsCount}
        onExport={onExport}
        isExporting={isExporting}
        exportDisabled={exportDisabled}
        exportTitle={exportTitle}
        onSaveSearch={onSaveSearch}
        saveSearchDisabled={saveSearchDisabled}
        saveSearchLabel={saveSearchLabel}
        showNaturalLanguageFilter={showNaturalLanguageFilter}
      />
    );
  }

  return <HorizontalFilterBarStandalone {...props} layoutTestId={layoutTestId} />;
}
